import * as React from 'react';
import { ICardSubcardsProps } from './types';
import {
    CLASS_CARD_DETAILS,
    CLASS_CARD_DETAILS_INCREASED_MARGINS_DROPDOWN,
    CLASS_CARD_DETAILS_SECTION
} from '../../../constants';
import {
    CARD_SECTION_SUBCARDS,
    CARD_SUBCARD_ADD,
    CARD_SUBCARDS_DONE,
    CARD_SUBCARDS_HIDE_DONE,
    CARD_SUBCARDS_PIN,
    CARD_SUBCARDS_SHOW_DONE,
    CARD_SUBCARDS_UNPIN
} from './constants';
import { CardSubcardsElements } from '../CardSubcardsElements/CardSubcardsElements';
import { SettingsSection } from '../../../../../base/components/SettingsSection/SettingsSection';
import { ESettingsSectionType } from '../../../../../base/components/SettingsSection/types';
import { SubcardsSearchSelectHOC } from '../../hocs/SubcardsSearchSelectHOC/SubcardsSearchSelectHOC';
import { SEARCH_SELECT_INPUT_SELECTOR } from '../../../../../base/components/SearchSelect/constants';
import { CLASS_SETTINGS_SECTION_EMPTY } from '../../../../../base/components/SettingsSection/constants';
import { Button, ButtonDropdown, Icon, SelectList, SelectListItem } from 'kui';
import { getAsidePanelTooltip } from '../../../../asidePanel/helpers/getAsidePanelTooltip';
import './_CardSubcards.scss';
import { getMessages } from '../../../../../../../store/constants';
import { AsidePanelContext } from '../../../../asidePanel/components/AsidePanel/constants';
import { EAsidePanelProperty } from '../../../../asidePanel/components/AsidePanel/types';
import { AnalyzeRender } from '../../../../../helpers/memoizeHelper';
import { TOOLTIP_ACTIONS_CARD } from '../../../../../main/kanbanView/wording';
import { ID_ASIDE_PANEL_ACTIONS_PORTAL } from '../../../../asidePanel/components/AsidePanelActionsButton/constants';

//@ts-ignore
const analyzeRender = window.Settings.development ? new AnalyzeRender(`CardSubcardsSection`) : null;

export function CardSubcardsSection({
    boardId,
    boardUsers,
    boardRoles,
    cardId,
    elements,
    isDefaultProperty,
    isPinned,
    sectionType,
    onClose,
    onDelete,
    onGantt,
    onShowBoardLimitSnackbarIfNeeded,
    onSort,
    onSubcardLink,
    onAddClicked,
    onClickDisallow,
    onPinToggle,
    onWeightChange,
    isHideDoneSubcards,
    onHideDoneChange,
    subcardsStats,
    ...attributes
    }: ICardSubcardsProps) {
    if (analyzeRender) analyzeRender.call(`CardSubcardsSection ${cardId}`);

    const { addShowedProperty, showedProperties, isCommentsColumn } = React.useContext(AsidePanelContext);
    const isDefault = sectionType === ESettingsSectionType.DEFAULT;
    const isPopup = sectionType === ESettingsSectionType.POPUP;
    const className = CLASS_CARD_DETAILS + '-subcards';
    const classActions = className + '__actions';

    const [isOpenedHook, setIsOpened] = React.useState(isPopup)
    const [isShow, setShow] = React.useState(false);
    const [isActionsOpen, setActionsOpen] = React.useState(null);

    const [_isPinned, setPinned] = React.useState(isPinned);
    const [_isHideDoneSubcards, setHideDoneSubcards] = React.useState(isHideDoneSubcards);
    React.useEffect(() => { // меняем только после закрытия, чтобы визуально не скакало
        setPinned(isPinned);
        setHideDoneSubcards(isHideDoneSubcards)
    }, [isActionsOpen]);

    const onAddOpened = () => {
        setIsOpened(true);
        onAddClicked();
    }
    const onAddClosed = () => {
        setIsOpened(false);
        if (onClose) onClose();
    }

    const onSelect = (action: () => void) => {
        action();
        setActionsOpen(false);
    };

    React.useEffect(() => {
        if ((elements && elements.length) || isDefaultProperty) {
            setShow(true);
            addShowedProperty(EAsidePanelProperty.CARD_SUBCARDS, isCommentsColumn);
        }
    }, [elements, isDefaultProperty]);

    if (
        !elements && sectionType === ESettingsSectionType.READONLY ||
        !isShow && !showedProperties.has(EAsidePanelProperty.CARD_SUBCARDS)
    ) {
        return;
    }

    const visibleElements = elements && elements.filter(element => {
        const isCardDone = Math.abs(element.processingPercent - 100) < Math.exp(-8) || element.processingStatus ==='done';
        return !isHideDoneSubcards || !isCardDone;
    });

    const isEmpty = !visibleElements || !visibleElements.length;

    return (
        <SettingsSection
            addTitle={CARD_SUBCARD_ADD}
            addComponent={
                isOpenedHook
                    ? <SubcardsSearchSelectHOC
                        cardId={cardId}
                        boardId={boardId}
                        isOpened={isOpenedHook}
                        isPopup={isPopup}
                        onAddClosed={onAddClosed}
                        sectionType={sectionType}
                    />
                    : <div></div>
            }
            className={`
                ${CLASS_CARD_DETAILS_SECTION}
                ${CLASS_CARD_DETAILS_SECTION}--subcards
                ${className}
                ${CLASS_CARD_DETAILS_INCREASED_MARGINS_DROPDOWN}
                ${isEmpty ? CLASS_SETTINGS_SECTION_EMPTY : ''}
            `}
            focusSelector={SEARCH_SELECT_INPUT_SELECTOR}
            isAddWide
            onClickDisallow={onClickDisallow}
            isOpened={isOpenedHook}
            title={CARD_SECTION_SUBCARDS}
            titleSub={!!elements && elements.length > 0 &&
                getMessages().getText(CARD_SUBCARDS_DONE, null, subcardsStats.donePercent, subcardsStats.doneCount, subcardsStats.totalCount)
            }
            titleButton={isDefault && elements && elements.length > 0 &&
                <ButtonDropdown
                    className={classActions}
                    dropdownClassName={className + '-dropdown'}
                    directionHorizontal={'right'}
                    opened={isActionsOpen}
                    onOpen={() => setActionsOpen(true)}
                    onClose={() => setActionsOpen(false)}
                    portal
                    portalId={ID_ASIDE_PANEL_ACTIONS_PORTAL}
                >
                    <Button
                        className={classActions + '-button'}
                        tooltip={getAsidePanelTooltip(TOOLTIP_ACTIONS_CARD)}
                        variant={'icon'}
                    >
                        <Icon xlink={'more'} size={24}/>
                    </Button>
                    <SelectList fixActive={false}>
                        <SelectListItem
                            icon={'pin'}
                            onClick={() => onSelect(onPinToggle)}
                            iconSize={24}
                        >
                            {_isPinned ? CARD_SUBCARDS_UNPIN : CARD_SUBCARDS_PIN}
                        </SelectListItem>
                        <SelectListItem
                            icon={'eye'}
                            onClick={() => onSelect(onHideDoneChange)}
                            iconSize={24}
                        >
                            {_isHideDoneSubcards ? CARD_SUBCARDS_SHOW_DONE : CARD_SUBCARDS_HIDE_DONE}
                        </SelectListItem>
                    </SelectList>
                </ButtonDropdown>
            }
            sectionType={sectionType}
            onAddOpened={onAddOpened}
            onAddClosed={onAddClosed}
            {...attributes}
        >
            {isEmpty ? <div />
                : <CardSubcardsElements
                    boardId={boardId}
                    elements={visibleElements}
                    boardUsers={boardUsers}
                    boardRoles={boardRoles}
                    sectionType={sectionType}
                    onDelete={onDelete}
                    onGantt={onGantt}
                    onSort={onSort}
                    onSubcardLink={onSubcardLink}
                    onWeightChange={onWeightChange}
                    onShowBoardLimitSnackbarIfNeeded={onShowBoardLimitSnackbarIfNeeded}
                />
            }
        </SettingsSection>
    );
};
